import {
  ArrowDefinition,
  BasicArrowPath,
  CircleDefinition,
  ClockwiseRotation,
  MeasureDefinition,
  PathBuilder,
  StyleForCircle,
  StyleForGraphFill,
  StyleForGraphLine,
  StyleForGraphPoint,
  StyleForGraphZeroLine,
  StyleForLine,
  StyleForMarker,
  StyleForPath,
  StyleForRect,
  StyleForText,
  TickDefinition,
} from "@common/components/Rendering";

// Viewport colors are defined in styling/viewport/SXViewPortStyles.scss as CSS variables,
// and mapped here to be used in the style definitions below.
// This way, the CSS variables are the single source of truth for viewport colors
export const viewportColors = {
  black: "var(--sx-viewport-black)",
  gray: "var(--sx-viewport-gray)",
  darkGray: "var(--sx-viewport-dark-gray)",
  // todo(Gustav): figure out the correct colors
  geomFillConcrete: "var(--sx-viewport-geom-fill)",
  geomFillTimber: "#F9E79F",
  geomFillSteel: "#EAEDED",
  measure: "var(--sx-viewport-measure)",
  pointFill: "var(--sx-viewport-point-fill)",
  selection: "var(--sx-viewport-selection)",

  deformationLine: "var(--sx-viewport-deformation-line)",
  momentFill: "var(--sx-viewport-moment-fill)",
  shearFill: "var(--sx-viewport-shear-fill)",
  normalFill: "var(--sx-viewport-normal-fill)",
  momentLine: "var(--sx-viewport-moment-line)",
  shearLine: "var(--sx-viewport-shear-line)",
  normalLine: "var(--sx-viewport-normal-line)",
  zeroLine: "var(--sx-viewport-zero-line)",

  stirrupLine: "var(--sx-viewport-stirrup-line)",
  stirrupFill: "var(--sx-viewport-stirrup-fill)",
  longReinfFill: "var(--sx-viewport-long-reinf-fill)",
  longReinfFlagged: "var(--sx-viewport-long-reinf-flagged)",

  // Experimental: fire exposure overlay colors.
  steelFireProtectionLine: "var(--sx-viewport-steel-fire-protection-line)",
  timberFireProtectionLine: "var(--sx-viewport-timber-fire-protection-line)",
  timberStructProtectionLine: "var(--sx-viewport-timber-struct-protection-line)",
  fireIconColor: "var(--sx-viewport-fire-icon-color)",

  // Load position indicator (buckling load position drawn in the cross-section).
  loadPositionUpperFill: "var(--sx-viewport-load-position-upper-fill)",
  loadPositionUpperStroke: "var(--sx-viewport-load-position-upper-stroke)",
  loadPositionLowerFill: "var(--sx-viewport-load-position-lower-fill)",
  loadPositionLowerStroke: "var(--sx-viewport-load-position-lower-stroke)",

  testColor: "var(--sx-test-red)",
} as const;

// ================================================================================================
// shared/common styles

export const geom_style_concrete: StyleForRect = {
  lineColor: viewportColors.gray,
  thickness: 1,
  fill: viewportColors.geomFillConcrete,
};

export const geom_style_timber: StyleForRect = {
  lineColor: viewportColors.gray,
  thickness: 1,
  fill: viewportColors.geomFillTimber,
};

export const geom_style_steel: StyleForRect = {
  lineColor: viewportColors.gray,
  thickness: 1,
  fill: viewportColors.geomFillSteel,
};

export const meassure_style: StyleForLine = {
  lineColor: viewportColors.measure,
  thickness: 1,
};
export const meassure_text_style: StyleForText = {
  lineColor: viewportColors.measure,
  textSize: 10,
};

// ================================================================================================
// Side view component

export const zero_line_style: StyleForGraphZeroLine = {
  lineColor: viewportColors.zeroLine,
  thickness: 2,
  dashed: true,
};

export const moment_zero_line_style: StyleForGraphZeroLine = {
  lineColor: viewportColors.momentLine,
  thickness: 2,
  dashed: true,
};
export const shear_zero_line_style: StyleForGraphZeroLine = {
  lineColor: viewportColors.shearLine,
  thickness: 2,
  dashed: true,
};
export const normal_zero_line_style: StyleForGraphZeroLine = {
  lineColor: viewportColors.normalLine,
  thickness: 2,
  dashed: true,
};

export const graph_point_style: StyleForGraphPoint = {
  color: {
    lineColor: viewportColors.black,
    thickness: 1,
    fill: viewportColors.pointFill,
  },
  radius: 0.02,
};

export const moment_line_color_style: StyleForGraphLine = {
  lineColor: viewportColors.momentLine,
  thickness: 1,
};
export const shear_line_color_style: StyleForGraphLine = {
  lineColor: viewportColors.shearLine,
  thickness: 1,
};
export const normal_line_color_style: StyleForGraphLine = {
  lineColor: viewportColors.normalLine,
  thickness: 1,
};
export const deformation_line_color_style: StyleForGraphLine = {
  lineColor: viewportColors.deformationLine,
  thickness: 2,
};

export const moment_fill_color_style: StyleForGraphFill = {
  fill: viewportColors.momentFill,
};
export const shear_fill_color_style: StyleForGraphFill = {
  fill: viewportColors.shearFill,
};
export const normal_fill_color_style: StyleForGraphFill = {
  fill: viewportColors.normalFill,
};
export const deformation_fill_color_style: StyleForGraphFill = { fill: null };

export const ground_style: StyleForLine = {
  lineColor: viewportColors.black,
  thickness: 3,
};
export const slash_style: StyleForLine = {
  lineColor: viewportColors.black,
  thickness: 1,
};
export const center_line_style: StyleForLine = {
  lineColor: viewportColors.black,
  thickness: 1,
  centre: true,
};
export const label_style: StyleForText = {
  lineColor: viewportColors.black,
  textSize: 12,
};

// Header above a section force diagram, e.g. "N [kN]". The color is set per diagram type by the
// caller so the header matches the curve it belongs to; kept smaller than label_style to stay discreet.
export const diagram_header_style: StyleForText = {
  lineColor: viewportColors.black,
  textSize: 11,
};

const reaction_force_color = "orange"; // todo(Gustav): determine color
export const reaction_force_style: StyleForLine = {
  lineColor: reaction_force_color,
  thickness: 2,
};
export const reaction_moment_style: StyleForCircle = {
  lineColor: reaction_force_color,
  thickness: 2,
  fill: null,
};

export const reaction_force_text: StyleForText = {
  lineColor: reaction_force_color,
  textSize: 12,
};

export const support_style: StyleForPath & StyleForCircle = {
  lineColor: viewportColors.black,
  thickness: 1,
  fill: null,
};

export const buckling_line_syle: StyleForPath = {
  lineColor: viewportColors.black,
  thickness: 1,
  fill: null,
  dashed: true,
};

const default_load_style: Omit<StyleForLine, "lineColor"> = {
  thickness: 1,
};
export const load_vertical_linear_style = default_load_style;
export const load_horizontal_linear_style = default_load_style;
export const load_vertical_point_style = default_load_style;
export const load_horizontal_point_style = default_load_style;

// viewport icons
export interface SlashStyle {
  size: number;
  min_count: number;
}
export interface IconSize {
  size: number; // main icon size
  extra: number; // extra size beyond icon size
}

export const viewport_icon_size: IconSize = {
  size: 0.2,
  extra: 0.1,
};
export const viewport_slash_size: SlashStyle = {
  size: 0.08,
  min_count: 3,
};

// [m] how long should the maximum force be
export const max_force_in_m = 0.7;

// ============================================================================
// CrossSectionView component

export const stirrup_style: StyleForRect = {
  lineColor: viewportColors.stirrupLine,
  thickness: 1,
  fill: viewportColors.stirrupFill,
};

export const long_reinf_style: StyleForCircle = {
  lineColor: viewportColors.black,
  thickness: 1,
  fill: viewportColors.longReinfFill,
};

/** A bar the exact-bar check refused a calculation over — see useExactBarMarks. */
export const long_reinf_flagged_style: StyleForCircle = {
  lineColor: viewportColors.longReinfFlagged,
  thickness: 2,
  fill: viewportColors.longReinfFlagged,
};

// Experimental: line thickness shared by the two "fire protection material" indicators (steel's
// per-face protection line and timber's protecting-material border) so they read as the same
// kind of indicator across materials. Kept separate from timber_struct_protection_style, which
// is a different indicator (the "protecting structure" switch) and stays thicker/solid.
const FIRE_PROTECTION_MATERIAL_THICKNESS = 1;

export const steel_fire_protection_style: StyleForPath = {
  // Experimental: style used to draw the steel fire protection indicator lines outside
  // the protected faces of the cross-section (see drawFireExposure.ts).
  lineColor: viewportColors.steelFireProtectionLine,
  thickness: FIRE_PROTECTION_MATERIAL_THICKNESS,
  fill: null,
  dashed: true,
};

export const timber_fire_protection_style: StyleForPath = {
  // Experimental: thin border drawn around the whole section when a protecting material is
  // selected in timber fire settings (see drawFireExposure.ts).
  lineColor: viewportColors.timberFireProtectionLine,
  thickness: FIRE_PROTECTION_MATERIAL_THICKNESS,
  fill: null,
  dashed: true,
};

export const timber_struct_protection_style: StyleForPath = {
  // Experimental: thicker line drawn outside a specific face when its "protecting structure"
  // switch is enabled in timber fire settings (see drawFireExposure.ts).
  lineColor: viewportColors.timberStructProtectionLine,
  thickness: 4,
  fill: null,
  dashed: false,
};

export const fire_icon_style: StyleForMarker = {
  // Experimental: style for the flame icon drawn outside each exposed face of a concrete/timber
  // section (see drawFireExposure.ts). Drawn as a flat single-color svg symbol (FireIconMarker
  // below) rather than the OS color emoji glyph, which rendered with a glossy/3D gradient.
  lineColor: viewportColors.fireIconColor,
  fill: null,
};

// Load position indicator: an hourglass of two triangles meeting at the load position, drawn on
// the vertical centerline of the section (see drawLoadPosition.ts). The upper triangle points
// down into the load position, the lower one points up into it.
//
// It is drawn as an svg symbol rather than as paths in drawing space: symbol contents are laid
// out in screen pixels, so the indicator keeps one fixed size for every section and at every
// zoom level - it marks a position, it does not measure anything. Each triangle is outlined in a
// darker shade of its own fill (see LoadPositionMarker below), the same fill/stroke pairing used
// by the other colored markers in this file (interaction_style_ok/interaction_style_fail), rather
// than a flat neutral outline - this style object itself carries no color.
export const load_position_style: StyleForMarker = {
  lineColor: undefined,
  fill: null,
};

// [px] width of the triangle bases and height of one triangle
const load_position_width = 15.75;
const load_position_triangle_height = 13.125;

function LoadPositionMarker(props: { id: ArrowId }) {
  const w = load_position_width / 2;
  const h = load_position_triangle_height;
  // svg y grows downwards, so the upper triangle is the one with negative y
  return (
    <symbol id={`${props.id}-sym`} overflow="visible">
      {/* <path
        d={`M ${-w},${-h} L ${w},${-h} L 0,0 Z`}
        fill={viewportColors.loadPositionUpperFill}
        stroke={viewportColors.loadPositionUpperStroke}
        strokeWidth={1}
      /> */}
      <path
        d={`M 0,0 L ${w},${h} L ${-w},${h} Z`}
        fill={viewportColors.loadPositionLowerFill}
        stroke={viewportColors.loadPositionLowerStroke}
        strokeWidth={1}
      />
    </symbol>
  );
}

// Experimental: flame icon drawn outside each exposed face of a concrete/timber section (see
// drawFireExposure.ts). Path/transform copied from @simplex/sx-icons' FireIcon (single flat-color
// path, viewBox 0 0 50 50), rather than the platform's color emoji glyph whose baked-in
// gradient/highlight shading read as too glossy/3D at small sizes.
const FIRE_ICON_SIZE = 20; // [px] rendered size, matches the previous emoji's apparent size
const FIRE_ICON_VIEWBOX = 50; // [px] the sx-icons FireIcon is authored in a 0 0 50 50 viewBox
const FIRE_ICON_SCALE = FIRE_ICON_SIZE / FIRE_ICON_VIEWBOX;
const FIRE_ICON_PATH =
  "M 381.63997,1200 C 135.77919,1061.434 71.049038,930.27865 108.05732,751.14866 135.37841,618.87726 224.83888,511.26304 233.417,379.24524 271.63184,448.78945 287.59935,498.9368 291.87036,571.60898 413.41348,422.69507 493.73121,216.54632 498.48692,0 c 0,0 316.57523,186.01008 337.34836,466.98023 27.25312,-57.91289 40.97132,-149.89172 13.7182,-209.5043 C 931.31098,317.09086 1409.8464,846.31428 784.73519,1200 902.263,971.16186 815.05535,662.38827 610.99652,519.78234 624.62426,581.10164 600.73114,809.80288 510.45434,910.29655 535.46754,742.38092 486.6538,671.37843 486.6538,671.37843 c 0,0 -16.75337,94.05444 -81.7575,189.06609 C 345.53708,947.20639 304.40709,1039.2914 381.64021,1200 z";

function FireIconMarker(props: { id: ArrowId }) {
  return (
    <symbol id={`${props.id}-sym`} overflow="visible">
      {/* center the 50x50 viewBox on the marker origin, then scale it to FIRE_ICON_SIZE */}
      <g transform={`translate(${-FIRE_ICON_SIZE / 2} ${-FIRE_ICON_SIZE / 2}) scale(${FIRE_ICON_SCALE})`}>
        {/* same inner transform as @simplex/sx-icons' FireIcon.tsx, applied to its raw path */}
        <g transform="translate(0 2.592) scale(0.03734762) translate(-71.049038 0)">
          <path d={FIRE_ICON_PATH} fill={viewportColors.fireIconColor} />
        </g>
      </g>
    </symbol>
  );
}

export const view_sel_style: StyleForText & StyleForPath = {
  lineColor: viewportColors.selection,
  thickness: 1,
  fill: null,
  textSize: 16,
};

export const view_style: StyleForText & StyleForPath = {
  lineColor: viewportColors.black,
  thickness: 1,
  fill: null,
  textSize: 16,
};

export const coordinate_axis_style: StyleForLine = {
  lineColor: viewportColors.gray,
  thickness: 1,
};

export const coordinate_axis_text_style: StyleForText = {
  lineColor: viewportColors.gray,
  textSize: 10,
};

// measure lines
/*
(all "extensions lines" are handled by markers/"arrows")
        a
======|   -------- (line going right)
column|           |
column|           |
column|           |  (ACTAUL horizontal line)
column|           |
column|     m     |
======|   -------- (line going right)
        a
*/
export const cs_meassure_dist = 0.05; // distance from column to the actual horizontal/vertical measurement line (m in illustration) alignmnet (a) is ignored
export const cs_alignment_dist = 0.01; // small distance from column to start of the "distancing" measurement lines (a in illustration)

// view direction (measure lines are ignored, take care not to overlap, )
/*

column|          / \
column|           |
column|           |   s
column|           |
column| d  -------/
column|       h     I
======|
*/
export const cs_view_dist = cs_meassure_dist + 0.1; // how far from the column should the hook start (d in illustration)
export const cs_view_dist_alt = 0.1; // how far from the column should the hook start (d in illustration) for sides without meassures
export const cs_hook_dist = 0.03; // how long should the hook be (h in the illustration)
export const cs_view_size = 0.03; // how long should the arrow be (s in the illustration)

export const cs_force_text: Omit<StyleForText, "lineColor"> = {
  textSize: 12,
};

// section/material name label, drawn below the cross section.
// same font as the load values (cs_force_text) above, just a bit bigger
export const cs_section_label_style: StyleForText = {
  lineColor: viewportColors.darkGray,
  textSize: 14,
};

// ============================================================================
// Isometric 3D viewport

// Shared visual dimensions for both the SVG and three.js isometric renderers.
// Result-board lane geometry remains in resultBoard.ts because it defines the
// diagram layout rather than its presentation.
export const isometric_3d = {
  colors: {
    outline: viewportColors.black,
    // grey hidden lines, from the "Hidden line grey" setting: 0% white, 100% black
    hiddenLine: (blackPercent: number) => {
      const channel = Math.round(255 * (1 - Math.min(Math.max(blackPercent, 0), 100) / 100));
      return `rgb(${channel}, ${channel}, ${channel})`;
    },
    // cardboards, lane zero lines and the member center line
    guide: `color-mix(in srgb, ${viewportColors.black} 35%, transparent)`,
    // Support solids are neutral. Hex only: shadeFaceColor() blends the shade pair itself.
    supportFace: "#e0e0e0",
    supportShadeLight: "#eeeeee",
    supportShadeDark: "#777777",
    // "debug extrusion face normals" arrows
    debugNormalFront: "#198754",
    debugNormalBack: "#dc3545",
    debugNormalEdgeOn: "#ffc107",
    // result diagrams (the deformation diagram has no fill)
    momentLine: viewportColors.momentLine,
    shearLine: viewportColors.shearLine,
    normalLine: viewportColors.normalLine,
    deformationLine: viewportColors.deformationLine,
    momentFill: viewportColors.momentFill,
    shearFill: viewportColors.shearFill,
    normalFill: viewportColors.normalFill,
  },
  axis: {
    xColor: "#d32f2f",
    yColor: "#2e7d32",
    zColor: "#1976d2",
    // light direction arrow, drawn from the origin along the light rays
    lightColor: "#f9a825",
    // dashed drop line from the light arrow's head to the zy plane
    lightProjectionColor: viewportColors.gray,
    lineThickness: 2,
    originRadius: 0.02,
    labelTextSize: 12,
  },
  board: {
    frameThickness: 0.8,
    guideThickness: 0.6,
    opacity: 0.35,
    dashSize: 0.05,
    dashGap: 0.03,
    loadPadding: 0.1,
  },
  diagram: {
    labelOffset: 0.08,
    momentLabelXOffset: 0.035,
    shearLabelXOffset: 0.035,
    pointRadius: 0.025,
    headerOffset: 0.12,
    fillOpacity: 0.45,
  },
  arrow: {
    maximumHeadLength: 0.12,
    headLengthRatio: 0.35,
    maximumHeadWidth: 0.07,
    headWidthRatio: 0.2,
  },
  buckling: {
    offset: 0.5,
    amplitude: 0.3,
  },
  // The solid support symbols have no sizes here: supportMeshes.ts draws them as percentages of a
  // single uniform SUPPORT_ICON_SIZE, so their proportions cannot be edited apart.
  load: {
    // gap between the longest arrow of one load case's lane and the start of the next
    laneSpacing: 0.2,
    horizontalAmplitude: 0.6,
    verticalAmplitude: 0.45,
    minimumAmplitude: 0.0,
    labelOffset: 0.05,
    verticalLabelOffset: 0.06,
    axialLaneInset: 0.1,
    distributedHorizontalStep: 0.6,
    distributedVerticalStep: 0.5,
    // dashed lines from the section centre to an eccentric vertical load's point of application,
    // drawn in the load case's colour
    eccentricityThickness: 1.2,
  },
} as const;

// ============================================================================
// IneractionDiagram component

export const interaction_zero_line: StyleForGraphLine = {
  lineColor: "#ddd",
};

export const interaction_tick_label: StyleForText = {
  lineColor: "#aaa",
  textSize: 10,
};

const interaction_medium_gray = "#737373";
export const interaction_graph_line: StyleForPath = {
  lineColor: interaction_medium_gray,
  fill: null,
};

/**
 * The colour the capacity curve is drawn in, per load combination type.
 *
 * The curve is not the same boundary for every type: a fire combination is checked with reduced
 * material properties on a reduced section, an accidental or seismic one with its own partial
 * factors, so each type has a curve of its own. The colour is what says which one is on screen.
 *
 * TYPE_ULTIMATE is the standard case and is deliberately absent - it keeps interaction_graph_line's
 * neutral grey, so the diagram the user sees most of the time looks exactly as it always has, and a
 * colour on the curve always means "this is not the ultimate curve".
 */
export const interaction_curve_color_fire = "#b45309";
export const interaction_curve_color_accidental = "#6d28d9";
export const interaction_curve_color_seismic = "#0f766e";

/** Caption naming a non-ultimate curve, drawn beside it in the curve's own colour. */
export const interaction_curve_caption = (lineColor: string): StyleForText => ({
  lineColor,
  textSize: 12,
});

export const axis_label_style: StyleForText = {
  lineColor: "#000",
  textSize: 12,
};

export const interaction_selected_lcomb_text: StyleForText = {
  lineColor: "#000",
  textSize: 12,
};

export const interaction_style_ok: StyleForMarker = {
  lineColor: "#1a7a1a",
  fill: "rgb(40,167,69)",
};
export const interaction_style_fail: StyleForMarker = {
  lineColor: "#8b0000",
  fill: "rgb(220,53,69)",
};
export const interaction_style_extended_selected: Pick<StyleForMarker, "lineColor"> = {
  lineColor: "#000",
};

export const interaction_tick_style: StyleForMarker = { lineColor: "#ddd", fill: null };
export const interaction_tick_vertical: ClockwiseRotation = { degrees: 0 };
export const interaction_tick_horizontal: ClockwiseRotation = { degrees: 90 };
export const interaction_point_style: StyleForMarker = {
  lineColor: interaction_medium_gray,
  fill: interaction_medium_gray,
};

///////////////////////////////////////////////////////////////////////////////
// Markers

export type ArrowId =
  | "view-direction-arrow"
  | "axis-arrow"
  | "reaction-force-arrow"
  | "measure"
  | "measure-extended"
  | "measure-point-hor"
  | "load-arrow"
  | "interaction-point"
  | "interaction-loadcase-point-selected"
  | "interaction-loadcase-point"
  | "axis-tick"
  | "load-position"
  | "fire-icon";

export const ColumnMarkers = () => [
  ArrowDefinition<ArrowId>("load-arrow", 10, "end", BasicArrowPath),
  ArrowDefinition<ArrowId>("reaction-force-arrow", 5, "end", BasicArrowPath),
  ArrowDefinition<ArrowId>("view-direction-arrow", 10, "start", BasicArrowPath, 1),
  ArrowDefinition<ArrowId>("axis-arrow", 5, "start", BasicArrowPath, 1),
  // todo(Gustav): should the point be a arrow or a circle?
  // ArrowDefinition<ArrowId>("interaction-loadcase-point-selected", 20, "end", (x) => ArrowPathWithRoundBottom(x, "badge"), 2),
  // ArrowDefinition<ArrowId>("interaction-loadcase-point", 10, "end", (x) => ArrowPathWithRoundBottom(x, "badge")),
  CircleDefinition<ArrowId>("interaction-loadcase-point-selected", 20, 2),
  CircleDefinition<ArrowId>("interaction-loadcase-point", 10),
  MeasureDefinition<ArrowId>("measure", 20, "extension+diagonal"),
  MeasureDefinition<ArrowId>("measure-extended", 20, "extension"),
  MeasureDefinition<ArrowId>("measure-point-hor", 20, "point"),
  CircleDefinition<ArrowId>("interaction-point", 3, 1),
  TickDefinition<ArrowId>("axis-tick", 10, 1),
  // padding stays 0: the symbol is small and the view direction indicators already extend far
  // beyond the section on every side, so it can never be clipped by the view inset.
  {
    id: "load-position" as ArrowId,
    padding: 0,
    generate: () => <LoadPositionMarker key="load-position" id="load-position" />,
  },
  {
    id: "fire-icon" as ArrowId,
    padding: 0,
    generate: () => <FireIconMarker key="fire-icon" id="fire-icon" />,
  },
];

const ArrowPathWithRoundBottom = (h: number, style: "badge" | "fat") =>
  new PathBuilder()
    .moveTo([-h, -h])
    .drawLineTo([h, 0])
    .drawLineTo([-h, h])
    .drawCubicSmooth([-h, -h], style === "fat" ? [-h * 2, 0] : [0, 0]);
